import { useCallback, useEffect, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router';
import { Plus, Trash2 } from 'lucide-react';
import { ApiError } from '../../lib/api';
import {
  operatorConsole,
  type PackBuilderLimits,
  type PackBuilderPack,
} from '../../lib/api/operatorConsole';
import { useMe } from '../../auth/MeContext';
import { Button } from '../../components/ui/Button';
import { Pill } from '../../components/ui/Pill';
import { Toast } from '../../components/ui/Toast';
import { useToast } from '../../components/ui/useToast';
import { formatMoney, formatPercent, healthLabel, isStaged, packStatusLabel } from './packBuilderFormat';

const COLUMNS = ['Pack', 'Price', 'RTP', 'Items', 'Status', 'Health', ''];

const headCell =
  'px-4 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-[var(--co-muted)]';
const cell = 'px-4 py-3 text-[13px] text-[var(--co-text)]';

export function MyPacksPage() {
  const { me } = useMe();
  const navigate = useNavigate();
  const operatorCurrency = me?.operator.currency ?? null;
  const [packs, setPacks] = useState<PackBuilderPack[]>([]);
  const [limits, setLimits] = useState<PackBuilderLimits | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hidden, setHidden] = useState(false);
  const { toast, show, dismiss } = useToast();

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await operatorConsole.packBuilder.listPacks();
      setPacks(res.packs);
      setLimits(res.limits);
    } catch (err) {
      // 404 = the operator has no pack builder (the backend hides the feature
      // entirely) — leave without a trace, like any unknown URL.
      if (err instanceof ApiError && err.status === 404) {
        setHidden(true);
        return;
      }
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleDelete(pack: PackBuilderPack) {
    if (
      !window.confirm(
        `Delete "${pack.title}"? The cards stay in the inventory; only the pack goes away.`,
      )
    ) {
      return;
    }
    try {
      await operatorConsole.packBuilder.deletePack(pack.id);
      setPacks((prev) => prev.filter((p) => p.id !== pack.id));
      show('success', 'Pack deleted');
    } catch (err) {
      show('error', err instanceof Error ? err.message : 'Could not delete the pack');
    }
  }

  if (hidden) return <Navigate to="/" replace />;

  const atLimit = limits !== null && packs.length >= limits.max_packs;

  return (
    <div className="space-y-4 p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">My Packs</h1>
          <p className="text-[12px] text-[var(--co-muted)]">
            Build packs from shared inventory, your own stock, or both. Nothing goes live until you
            hit publish.
          </p>
          {limits && (
            <p className="text-[11px] text-[var(--co-muted)]">
              {packs.length} of {limits.max_packs} packs
            </p>
          )}
        </div>
        <Button
          onClick={() => navigate('/my-packs/new')}
          disabled={atLimit}
          title={atLimit ? 'Pack limit reached' : 'Create a pack'}
          className="gap-2"
        >
          <Plus size={16} aria-hidden="true" />
          Create a new pack
        </Button>
      </div>

      {loading ? (
        <p className="text-[13px] text-[var(--co-muted)]">Loading packs…</p>
      ) : error ? (
        <p className="text-[13px] text-[var(--co-error)]">Error: {error}</p>
      ) : packs.length === 0 ? (
        <div className="rounded-xl border border-[var(--co-border)] bg-[var(--co-surface)] p-6 text-center text-[13px] text-[var(--co-muted)]">
          No packs yet — start with “Create a new pack”.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-[var(--co-border)] bg-[var(--co-surface)]">
          <table aria-label="My packs" className="w-full border-collapse">
            <thead>
              <tr className="border-b border-[var(--co-border)]">
                {COLUMNS.map((column, index) => (
                  <th key={column || `spacer-${index}`} scope="col" className={headCell}>
                    {column}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {packs.map((pack) => {
                const status = packStatusLabel(pack);
                const health = healthLabel(pack.health);
                // Only a staged pack is deletable: enabled/restocking are published
                // (CONTRACT §4.8) and `disabled` is support-controlled.
                const deletable = isStaged(pack);
                return (
                  <tr key={pack.id} className="border-b border-[var(--co-border)] last:border-b-0">
                    <td className={cell}>
                      <Link
                        to={`/my-packs/${pack.id}?tab=details`}
                        className="font-medium hover:underline"
                      >
                        {pack.title}
                      </Link>
                      <span className="ml-2 font-mono text-[11px] text-[var(--co-muted)]">
                        #{pack.id}
                      </span>
                    </td>
                    <td className={`${cell} font-mono`}>
                      {formatMoney(pack.price, pack.currency ?? operatorCurrency)}
                    </td>
                    <td className={`${cell} font-mono`}>{formatPercent(pack.stated_rtp)}</td>
                    <td className={`${cell} font-mono`}>{pack.items_count}</td>
                    <td className={cell}>
                      <Pill tone={status.tone}>{status.label}</Pill>
                    </td>
                    <td className={cell}>
                      <Pill
                        tone={health.tone}
                        title={
                          pack.health
                            ? `Checked ${new Date(pack.health.checked_at).toLocaleString()}`
                            : undefined
                        }
                      >
                        {health.label}
                      </Pill>
                    </td>
                    <td className={`${cell} text-right`}>
                      {/* A live pack must be unpublished first (backend: 409);
                          a restocking one cannot even be unpublished, and a
                          support-disabled one is not the operator's to remove. */}
                      {deletable && (
                        <button
                          type="button"
                          onClick={() => void handleDelete(pack)}
                          aria-label={`Delete ${pack.title}`}
                          title="Delete pack"
                          className="rounded p-1 text-[var(--co-muted)] transition-colors hover:bg-[var(--co-bg)] hover:text-[var(--co-error)]"
                        >
                          <Trash2 size={14} aria-hidden="true" />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Toast toast={toast} onDismiss={dismiss} />
    </div>
  );
}
